import React, { useCallback, useEffect, useState } from 'react';
import ConsoleShell from '../../components/admin2/ConsoleShell';
import { adminWaitingShipments } from '../../lib/marketOrdersApi';
import { formatSar } from '../../lib/marketPricing';

// «الشحنات المنتظرة» (owner, 2026-10-29): paid orders of the delivered-price
// model (air / sea) that are procured from HioBuy as usual but not shipped by
// it — they wait for our forwarder. Each with its weight (air) or volume (sea)
// and how long it has waited; running totals per method, so the team decides
// when a shipment is worth sending.

const T = {
  ar: { title: 'الشحنات المنتظرة', sub: 'طلبات الشحن الجوي والبحري المدفوعة التي لم تُشحن بعد — تُجمَّع وتُرسل عبر شركة الشحن.',
    air: 'جوي', sea: 'بحري', orders: 'طلبات', kg: 'كجم', cbm: 'م³', goods: 'قيمة البضاعة', oldest: (d) => `الأقدم: ${d} يوم`,
    order: 'الطلب', status: 'الحالة', waiting: 'ينتظر', days: (d) => `${d} يوم`, size: 'الوزن / الحجم', units: 'وحدات', total: 'الإجمالي', city: 'المدينة',
    storage: 'التخزين المجاني', storageLeft: (d) => (d < 0 ? 'انتهى' : `باقي ${d} يوم`), soonest: (d) => `أقرب انتهاء تخزين: ${d < 0 ? 'انتهى' : `${d} يوم`}`,
    none: 'لا توجد طلبات منتظرة.', reload: 'تحديث' },
  en: { title: 'Waiting shipments', sub: 'Paid air and sea orders not shipped yet — consolidated and sent through the forwarder.',
    air: 'Air', sea: 'Sea', orders: 'orders', kg: 'kg', cbm: 'm³', goods: 'Goods value', oldest: (d) => `Oldest: ${d} days`,
    order: 'Order', status: 'Status', waiting: 'Waiting', days: (d) => `${d} days`, size: 'Weight / volume', units: 'Units', total: 'Total', city: 'City',
    storage: 'Free storage', storageLeft: (d) => (d < 0 ? 'Expired' : `${d} days left`), soonest: (d) => `Soonest storage expiry: ${d < 0 ? 'expired' : `${d} days`}`,
    none: 'No waiting orders.', reload: 'Refresh' },
};
T.zh = T.en;

// HioBuy's free storage (60 days, then ¥5 a parcel): red at 10 days or less, amber at 20
const storageColor = (d) => (d == null ? undefined : d <= 10 ? '#B4372C' : d <= 20 ? '#9A6B12' : '#2E7D32');

export default function ConsoleMarketWaiting({ user, profile, lang }) {
  const isAr = lang === 'ar';
  const c = T[lang] || T.ar;
  const [data, setData] = useState(null);
  const [err, setErr] = useState('');
  const load = useCallback(async () => {
    setData(null); setErr('');
    const r = await adminWaitingShipments();
    if (!r.ok) { setErr(r.error); setData({ totals: {}, rows: [] }); return; }
    setData(r.data);
  }, []);
  useEffect(() => { load(); }, [load]);
  const t = data?.totals || {};
  const rows = data?.rows || [];
  const num = (n, d = 2) => Number(n || 0).toLocaleString('en-US', { maximumFractionDigits: d });
  return (
    <ConsoleShell user={user} profile={profile} lang={lang} active="market-waiting">
      <div className="ac-page" style={{ fontFamily: isAr ? 'var(--font-ar)' : 'var(--font-sans)' }}>
        <div className="ac-page-head">
          <div>
            <h1 className="ac-h1">{c.title}</h1>
            <p className="ac-sub">{c.sub}</p>
          </div>
          <button type="button" className="ac-btn ac-btn-sm" onClick={load}>{c.reload}</button>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: 12, marginBottom: 14 }}>
          {['air', 'sea'].map((m) => (
            <div key={m} className="ac-card" data-waiting-total={m}>
              <div className="ac-card-title">{c[m]}</div>
              <div style={{ fontSize: 24, fontWeight: 700 }} dir="ltr">
                {m === 'air' ? `${num(t.air?.kg)} ${c.kg}` : `${num(t.sea?.cbm, 3)} ${c.cbm}`}
              </div>
              <div style={{ fontSize: 13, color: '#666' }}>
                {num(t[m]?.orders, 0)} {c.orders} · {c.goods}: {formatSar(Number(t[m]?.goods_sar || 0))} · {c.oldest(num(t[m]?.oldest_days, 0))}
              </div>
              {t[m]?.soonest_storage_days_left != null && (
                <div data-waiting-soonest={t[m].soonest_storage_days_left} style={{ fontSize: 13, fontWeight: 700, color: storageColor(t[m].soonest_storage_days_left) }}>
                  {c.soonest(t[m].soonest_storage_days_left)}
                </div>
              )}
            </div>
          ))}
        </div>
        {err && <div className="ac-card" style={{ color: '#B4372C', marginBottom: 12 }}>{err}</div>}
        {data == null ? <div className="ac-skel" style={{ height: 120 }} /> : rows.length === 0 ? <div className="ac-placeholder">{c.none}</div> : (
          <div className="ac-card" style={{ overflowX: 'auto' }}>
            <table className="ac-table" data-waiting-rows={rows.length} style={{ width: '100%', fontSize: 13 }}>
              <thead>
                <tr><th>{c.order}</th><th>{c.air}/{c.sea}</th><th>{c.status}</th><th>{c.waiting}</th><th>{c.size}</th><th>{c.units}</th><th>{c.total}</th><th>{c.city}</th><th>{c.storage}</th></tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={r.id} data-waiting-order={r.order_no}>
                    <td dir="ltr">{r.order_no}</td>
                    <td>{c[r.method]}</td>
                    <td>{r.status}</td>
                    <td>{c.days(num(r.days_waiting, 0))}</td>
                    <td dir="ltr">{r.method === 'sea' ? `${num(r.cbm, 4)} ${c.cbm}` : `${num(r.kg, 2)} ${c.kg}`}</td>
                    <td>{num(r.units, 0)}</td>
                    <td>{formatSar(Number(r.total_sar))}</td>
                    <td>{r.city || '—'}</td>
                    <td data-storage-days-left={r.storage_days_left ?? ''} style={{ fontWeight: 700, color: storageColor(r.storage_days_left) }}>
                      {r.storage_days_left == null ? '—' : c.storageLeft(r.storage_days_left)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </ConsoleShell>
  );
}
